/**
 * GanttDetailDrawer.tsx
 * Read-only details panel for a Master-Gantt bar.
 *
 * Opened by a single click on a leaf bar (machineBooking / employeeAssignment)
 * or a conflict bar. Replaces the old hard-navigate-on-click: instead of
 * jumping straight to the Machine / Employee Schedule, the user gets a curated,
 * read-only summary and an explicit "Open in <X> Schedule" button that performs
 * the deep-link (and the row scroll-to + highlight) only when they ask for it.
 *
 * Strictly read-only — no edit fields, no service calls, no mutations.
 * Modelled on BookingDetailDrawer's header / body / footer structure and the
 * sage theme tokens, but with every editable control removed.
 */

import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  Chip,
  Divider,
  IconButton,
  Button,
  Stack,
} from '@mui/material';
import { Close, OpenInNew, WarningAmberOutlined } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import Highcharts from 'highcharts/highstock';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Fully-resolved, presentation-ready detail for a single Gantt bar.
 * Built by the caller (MasterGanttPage) from the Master-Gantt response so this
 * component stays free of data-shape knowledge.
 */
export interface GanttBarDetail {
  kind: 'machineBooking' | 'employeeAssignment';
  title: string;
  status: string | null;
  source: string | null;
  eventType?: string | null;
  machine?: string | null;
  person?: string | null;
  task?: string | null;
  project?: string | null;
  workOrder?: string | null;
  labSection?: string | null;
  /** Unix ms (UTC midnight) start, or null. */
  start: number | null;
  /** Unix ms (UTC midnight) inclusive end, or null. */
  end: number | null;
  /** 'hard' | 'soft' | null — when set, a conflict note is shown. */
  conflictType?: 'hard' | 'soft' | null;
  /** Whether the "Open in Schedule" footer button is shown. */
  canOpenInSchedule: boolean;
  /** Label for that button, e.g. "Open in Machine Schedule". */
  openInScheduleLabel: string;
}

interface GanttDetailDrawerProps {
  open: boolean;
  detail: GanttBarDetail | null;
  onClose: () => void;
  /** Performs the deep-link (Machine / Employee Schedule) with row highlight. */
  onOpenInSchedule: () => void;
  /**
   * P2-21 (audit #2): stat-holiday ISO dates ('YYYY-MM-DD') from the page's
   * useScheduleHolidays feed — the Staffing "busy N working day(s)" row
   * subtracts them so it agrees with the holiday-aware staffing surfaces.
   * Omitted/empty ⇒ Mon–Fri approximation (graceful degrade).
   */
  holidays?: ReadonlySet<string>;
}

// ---------------------------------------------------------------------------
// Internal presentation helpers
// ---------------------------------------------------------------------------

/** Uppercase caption label for a field row. */
const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      fontWeight: 700,
      color: 'text.secondary',
      textTransform: 'uppercase',
      letterSpacing: 0.4,
    }}
  >
    {children}
  </Typography>
);

/** A single Label / Value row; renders "—" when the value is empty. */
const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline' }}>
    <Box sx={{ width: 96, flexShrink: 0 }}>
      <FieldLabel>{label}</FieldLabel>
    </Box>
    <Typography variant="body2" sx={{ wordBreak: 'break-word', minWidth: 0 }}>
      {value == null || value === '' ? '—' : value}
    </Typography>
  </Box>
);

/** 'YYYY-MM-DD'-style human date from Unix ms; "—" for null. */
function fmtDate(ms: number | null): string {
  return ms != null ? Highcharts.dateFormat('%e %b %Y', ms) : '—';
}

/**
 * Wave 3 / P2-21 (audit #2) — working-day count across [startMs, endMs]
 * inclusive (UTC-midnight ms, as built by toMs/toEndMs): Mon–Fri MINUS the stat
 * holidays in `holidays` (ISO 'YYYY-MM-DD' set — the page's useScheduleHolidays
 * feed). With the feed threaded in, this agrees with every other holiday-aware
 * staffing surface (matrix, popup, pickers, chat) instead of over-counting a
 * Canada-Day week as 5. An empty/absent set degrades to Mon–Fri only.
 */
function workingDaysBetweenMs(
  startMs: number,
  endMs: number,
  holidays?: ReadonlySet<string>,
): number {
  const DAY = 86_400_000;
  let n = 0;
  for (let t = startMs; t <= endMs; t += DAY) {
    const dt = new Date(t);
    const dow = dt.getUTCDay(); // 0=Sun … 6=Sat
    const iso = dt.toISOString().slice(0, 10);
    if (dow !== 0 && dow !== 6 && !holidays?.has(iso)) n += 1;
  }
  return n;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const GanttDetailDrawer: React.FC<GanttDetailDrawerProps> = ({
  open,
  detail,
  onClose,
  onOpenInSchedule,
  holidays,
}) => {
  const theme = useTheme();

  if (!detail) {
    return <Drawer anchor="right" open={open} onClose={onClose} />;
  }

  const isMachine = detail.kind === 'machineBooking';
  const typeLabel = isMachine ? 'Machine booking' : 'Employee allocation';
  const statusColor = detail.status === 'Committed' ? 'primary' : 'default';
  const dateRange = `${fmtDate(detail.start)} – ${fmtDate(detail.end)}`;
  const conflictColor =
    detail.conflictType === 'hard'
      ? theme.schedule.conflictHard
      : theme.schedule.conflictSoft;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 360, maxWidth: '92vw' } }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 2, pb: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.25 }}>
              {detail.title}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
              {detail.status && (
                <Chip
                  size="small"
                  label={detail.status}
                  color={statusColor}
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              )}
              {detail.source && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={detail.source}
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              )}
            </Stack>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close details">
            <Close fontSize="small" />
          </IconButton>
        </Box>

        <Divider />

        {/* ── Body ────────────────────────────────────────────────────────── */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
          }}
        >
          {detail.conflictType && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                p: 1,
                borderRadius: `${theme.border.radius}px`,
                border: `1px solid ${conflictColor}`,
                color: conflictColor,
              }}
            >
              <WarningAmberOutlined fontSize="small" />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {detail.conflictType === 'hard'
                  ? 'Hard conflict — overlaps another booking on this machine.'
                  : 'Soft conflict — this machine may not be capable of the task.'}
              </Typography>
            </Box>
          )}

          <DetailRow label="Type" value={typeLabel} />
          {isMachine && <DetailRow label="Machine" value={detail.machine} />}
          {!isMachine && <DetailRow label="Person" value={detail.person} />}
          <DetailRow label="Task" value={detail.task} />
          <DetailRow label="Project" value={detail.project} />
          <DetailRow label="Work Order" value={detail.workOrder} />
          {isMachine && <DetailRow label="Lab Section" value={detail.labSection} />}
          <DetailRow label="Status" value={detail.status} />
          <DetailRow label="Source" value={detail.source} />
          {!isMachine && (
            // Wave 3: the "Allocation N%" row is retired — staffing reads as
            // busy DAYS. P2-21: holiday-aware via the page's holiday feed, so
            // this count matches the Employee Schedule popup for the same bar.
            <DetailRow
              label="Staffing"
              value={
                detail.start != null && detail.end != null
                  ? `busy ${workingDaysBetweenMs(detail.start, detail.end, holidays)} working day(s)`
                  : 'busy on the covered working days'
              }
            />
          )}
          {isMachine && detail.eventType && (
            <DetailRow label="Event" value={detail.eventType} />
          )}
          <DetailRow label="Dates" value={dateRange} />
        </Box>

        <Divider />

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>
            Close
          </Button>
          {detail.canOpenInSchedule && (
            <Button
              variant="contained"
              endIcon={<OpenInNew />}
              onClick={onOpenInSchedule}
              sx={{ textTransform: 'none' }}
            >
              {detail.openInScheduleLabel}
            </Button>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};
